import React, { useState, useEffect } from 'react';
import { 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow, 
    Paper, 
    CircularProgress, 
    Typography, 
    Box, 
    Tooltip, 
    IconButton, 
    Button, 
    Chip, 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import WarningIcon from '@mui/icons-material/Warning';
import CloseIcon from '@mui/icons-material/Close';
import { formatCurrency, formatNumber, hasLowPerformance, calculateRoas } from './utils';
import CampaignDetailAnalysis from './CampaignDetailAnalysis';

/**
 * Campaign Table Component
 * @param {Object} props - Component props
 * @param {Array} props.reports - Campaign reports data (already filtered if needed)
 * @param {Function} props.onSelectCampaign - Callback when a campaign is selected
 * @param {string} props.selectedCampaign - Currently selected campaign ID
 * @param {Array} props.campaignStatuses - Campaign status data
 * @param {Function} props.onPauseCampaign - Callback to pause a campaign
 * @param {Function} props.onActivateCampaign - Callback to activate a campaign
 * @param {boolean} props.isFiltered - Whether the reports are already filtered
 * @param {number} props.totalReportsCount - Total number of reports before filtering
 * @param {Object} props.dateRange - Current date range with startDate and endDate
 */
const CampaignTable = ({ 
    reports, 
    onSelectCampaign, 
    selectedCampaign, 
    campaignStatuses, 
    onPauseCampaign, 
    onActivateCampaign,
    isFiltered = false,
    totalReportsCount = 0,
    dateRange
}) => {
    // Log when component receives new reports
    useEffect(() => {
        console.debug('CampaignTable received reports:', reports.length, 'isFiltered:', isFiltered);
    }, [reports, isFiltered]);
    
    // Add state for sorting
    const [sortConfig, setSortConfig] = useState({
        key: null,
        direction: 'asc'
    });
    
    // Add state to track which campaigns are currently being toggled
    const [togglingCampaigns, setTogglingCampaigns] = useState({});

    // Add state to track which campaign is selected for detailed analysis
    const [detailCampaign, setDetailCampaign] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);

    // Sort the reports based on sortConfig
    const sortedReports = React.useMemo(() => {
        // First, group the reports by sub_id and keyword
        const groupedReports = {};
        
        reports.forEach(report => {
            const key = `${report.sub_id}-${report.keyword}`;
            
            if (!groupedReports[key]) {
                // Initialize a new group with the first report's data
                groupedReports[key] = {
                    ...report,
                    // Initialize numeric fields for aggregation
                    clicks: parseFloat(report.clicks) || 0,
                    conversions: parseFloat(report.conversions) || 0,
                    cost: parseFloat(report.cost) || 0,
                    revenue: parseFloat(report.revenue) || 0,
                    total_revenue: parseFloat(report.total_revenue) || 0,
                    taboola_clicks: parseFloat(report.taboola_clicks) || 0,
                    original_payout: parseFloat(report.payout) || 0, // Store original payout for reference
                    // Track dates for this group
                    dates: [report.date],
                    // Keep original report for reference
                    originalReports: [report],
                    // Initialize TQ values for aggregation
                    tq_sum: parseFloat(report.tq_sum) || parseFloat(report.tq) || 0,
                    tq_count: parseFloat(report.tq_count) || (parseFloat(report.tq) > 0 ? 1 : 0),
                    // Initialize searches and hits for coverage calculation
                    searches: parseFloat(report.searches) || 0,
                    hits: parseFloat(report.hits) || 0
                };
            } else {
                // Add to existing group
                const group = groupedReports[key];
                
                // Aggregate numeric values
                group.clicks += parseFloat(report.clicks) || 0;
                group.conversions += parseFloat(report.conversions) || 0;
                group.cost += parseFloat(report.cost) || 0;
                group.revenue += parseFloat(report.revenue) || 0;
                group.total_revenue += parseFloat(report.total_revenue) || 0;
                group.taboola_clicks += parseFloat(report.taboola_clicks) || 0;
                
                // Aggregate searches and hits for coverage calculation
                group.searches += parseFloat(report.searches) || 0;
                group.hits += parseFloat(report.hits) || 0;
                
                // Add date if not already included
                if (!group.dates.includes(report.date)) {
                    group.dates.push(report.date);
                }
                
                // Add original report to the collection
                group.originalReports.push(report);
                
                // Aggregate TQ values if available
                if (report.tq_sum !== undefined) {
                    group.tq_sum += parseFloat(report.tq_sum) || 0;
                    group.tq_count += parseFloat(report.tq_count) || 0;
                } else if (parseFloat(report.tq) > 0) {
                    group.tq_sum += parseFloat(report.tq) || 0;
                    group.tq_count += 1;
                }
            }
        });
        
        // Calculate derived metrics for each group
        Object.values(groupedReports).forEach(group => {
            // Special debug for campaign 44123876
            if (group.sub_id === '44123876') {
                console.debug('Processing campaign 44123876 in CampaignTable:', {
                    rawGroup: { ...group },
                    revenue: parseFloat(group.revenue) || 0, // Used for ROAS calculation
                    total_revenue: parseFloat(group.total_revenue) || 0, // Used for payout calculation
                    cost: parseFloat(group.cost) || 0
                });
            }
            
            // Calculate CPC directly as cost/taboola_clicks
            const taboola_clicks = parseFloat(group.taboola_clicks) || 0;
            group.cpc = taboola_clicks > 0 ? group.cost / taboola_clicks : 0;
            
            // Calculate CVR directly as (conversions/taboola_clicks)*100
            const conversions = parseFloat(group.conversions) || 0;
            group.cvr = taboola_clicks > 0 ? (conversions / taboola_clicks) * 100 : 0;
            
            // Calculate ROAS as revenue/cost (simpler and more standard formula)
            // NOTE: This uses revenue (not total_revenue) for ROAS calculation
            const revenue = parseFloat(group.revenue) || 0;
            const cost = parseFloat(group.cost) || 0;
            group.roas = cost > 0 ? revenue / cost : 0;
            
            // Calculate coverage as searches/hits if available
            if (group.searches !== undefined && group.hits !== undefined) {
                const searches = parseFloat(group.searches) || 0;
                const hits = parseFloat(group.hits) || 0;
                group.coverage = hits > 0 ? (searches / hits) * 100 : 0;
            }
            
            // Calculate payout as total_revenue/sum(clicks) instead of averaging individual payouts
            // This represents the average revenue generated per click across the entire period
            // IMPORTANT: We calculate this directly from total_revenue and clicks, NOT from any pre-calculated payout value
            group.payout = group.clicks > 0 ? group.total_revenue / group.clicks : 0;
            
            // For TQ, get the most recent date report and calculate from tq_sum/tq_count
            const sortedDates = [...group.dates].sort((a, b) => new Date(b) - new Date(a));
            
            // Get today's date in YYYY-MM-DD format
            const today = new Date().toISOString().split('T')[0];
            
            // Find the first date that's not today
            const mostRecentDate = sortedDates.find(date => date !== today);
            
            // Find the report for the most recent date (excluding today)
            const mostRecentReport = group.originalReports.find(report => report.date === mostRecentDate);
            
            if (mostRecentReport) {
                // Calculate TQ from tq_sum and tq_count in the most recent report
                const tqSum = parseFloat(mostRecentReport.tq_sum) || 0;
                const tqCount = parseFloat(mostRecentReport.tq_count) || 0;
                const tqValue = tqCount > 0 ? tqSum / tqCount : 0;
                
                // Store and use the calculated value
                group.original_tq = tqValue;
                group.tq = tqValue;
                group.tq_status = tqValue > 0 ? 'valid' : 'zero';
                
                // Log TQ calculation for debugging
                if (group.sub_id === '44123876') {
                    console.debug('TQ calculation for campaign 44123876:', {
                        most_recent_date: mostRecentDate,
                        tq_sum: tqSum,
                        tq_count: tqCount,
                        calculated_tq: tqValue,
                        status: group.tq_status
                    });
                }
            } else {
                // No recent report found with a date, use aggregated values
                const tqSum = parseFloat(group.tq_sum) || 0;
                const tqCount = parseFloat(group.tq_count) || 0;
                group.original_tq = tqCount > 0 ? tqSum / tqCount : 0;
                group.tq = group.original_tq;
                group.tq_status = group.tq > 0 ? 'valid' : 'zero';
                
                // Log TQ calculation as fallback for debugging
                if (group.sub_id === '44123876') {
                    console.debug('TQ calculation for campaign 44123876 (FALLBACK):', {
                        dates_available: group.dates,
                        tq_sum: tqSum,
                        tq_count: tqCount,
                        calculated_tq: group.original_tq,
                        status: group.tq_status
                    });
                }
            }
            
            // Format date range
            group.dateRange = group.dates.length > 1 ? 
                `${group.dates[0]} to ${group.dates[group.dates.length - 1]}` : 
                group.dates[0];
            
            // Special debug for campaign 44123876 after calculations
            if (group.sub_id === '44123876') {
                console.debug('Campaign 44123876 after processing in CampaignTable:', {
                    calculatedRoas: group.roas,
                    roasFormula: `revenue(${revenue}) / cost(${cost}) = ${group.roas}`,
                    payoutFormula: `total_revenue(${group.total_revenue}) / clicks(${group.clicks}) = ${group.payout}`,
                    calculatedCpc: group.cpc,
                    calculatedCvr: group.cvr,
                    payout: group.payout,
                    dateRange: group.dateRange
                });
            }
        });
        
        console.debug('Grouped reports:', {
            originalCount: reports.length,
            groupedCount: Object.keys(groupedReports).length
        });
        
        // Convert grouped reports object to array and filter out campaigns with cost of 0
        let sortableReports = Object.values(groupedReports).filter(report => report.cost > 0);
            
        console.debug('Processing grouped reports in CampaignTable:', {
            totalReports: reports.length,
            groupedReports: sortableReports.length,
            isLocallyFiltered: reports.length < totalReportsCount,
            filteredOutZeroCost: Object.keys(groupedReports).length - sortableReports.length
        });
        
        // Debug: Check how many reports have low performance
        const lowPerformanceReports = sortableReports.filter(report => hasLowPerformance(report));
        console.debug(`CampaignTable: ${lowPerformanceReports.length} of ${sortableReports.length} grouped reports have low performance indicators`);
        
        // Debug: Log the first few reports' performance metrics
        if (sortableReports.length > 0) {
            const sampleReports = sortableReports.slice(0, 3);
            console.debug('Sample grouped reports performance metrics:', sampleReports.map(report => ({
                sub_id: report.sub_id,
                keyword: report.keyword,
                dateRange: report.dateRange,
                payout: report.clicks > 0 ? report.total_revenue / report.clicks : 0,
                cvr: report.cvr,
                roas: report.roas,
                isLowPerformance: hasLowPerformance(report)
            })));
        }
        
        if (sortConfig.key) {
            sortableReports.sort((a, b) => {
                // Handle numeric values - ensure proper type conversion
                let aValue, bValue;
                
                // Special handling for string fields
                if (sortConfig.key === 'sub_id' || sortConfig.key === 'keyword' || sortConfig.key === 'date') {
                    aValue = (a[sortConfig.key] || '').toString().toLowerCase();
                    bValue = (b[sortConfig.key] || '').toString().toLowerCase();
                    
                    // For string comparison
                    if (aValue < bValue) {
                        return sortConfig.direction === 'asc' ? -1 : 1;
                    }
                    if (aValue > bValue) {
                        return sortConfig.direction === 'asc' ? 1 : -1;
                    }
                    return 0;
                } else {
                    // For numeric fields, ensure proper conversion
                    aValue = parseFloat(a[sortConfig.key]) || 0;
                    bValue = parseFloat(b[sortConfig.key]) || 0;
                    
                    // For numeric comparison
                    if (aValue < bValue) {
                        return sortConfig.direction === 'asc' ? -1 : 1;
                    }
                    if (aValue > bValue) {
                        return sortConfig.direction === 'asc' ? 1 : -1;
                    }
                    return 0;
                }
            });
        }
        return sortableReports;
    }, [reports, sortConfig, totalReportsCount]);

    // Handle column header click for sorting
    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    // Get sort direction indicator
    const getSortDirectionIndicator = (key) => {
        if (sortConfig.key !== key) {
            return null;
        }
        return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
    };

    // Handle filter icon click with toggle functionality
    const handleFilterClick = (campaignId) => {
        // If the clicked campaign is already selected, unselect it (set to null)
        if (selectedCampaign === campaignId) {
            onSelectCampaign(null);
        } else {
            onSelectCampaign(campaignId);
        }
    };

    // Handle campaign status toggle
    const handleToggleStatus = (campaignId) => {
        const campaignStatus = campaignStatuses.find(status => status.id === campaignId);
        if (campaignStatus) {
            // Set the campaign as toggling
            setTogglingCampaigns(prev => ({ ...prev, [campaignId]: true }));
            
            if (campaignStatus.is_active) {
                onPauseCampaign(campaignId).finally(() => {
                    // Remove the toggling state when done
                    setTogglingCampaigns(prev => ({ ...prev, [campaignId]: false }));
                });
            } else {
                onActivateCampaign(campaignId).finally(() => {
                    // Remove the toggling state when done
                    setTogglingCampaigns(prev => ({ ...prev, [campaignId]: false }));
                });
            }
        }
    };

    // Get campaign status - improved version with better matching
    const getCampaignStatus = (campaignId) => {
        if (!campaignId || !campaignStatuses || campaignStatuses.length === 0) {
            return null;
        }
        
        // Try exact match first
        let campaignStatus = campaignStatuses.find(status => status.id === campaignId);
        
        // If no match, try case-insensitive match
        if (!campaignStatus) {
            campaignStatus = campaignStatuses.find(status => 
                status.id.toLowerCase() === campaignId.toLowerCase()
            );
        }
        
        // If still no match, try to match by name
        if (!campaignStatus) {
            campaignStatus = campaignStatuses.find(status => {
                // Check if campaign name contains the sub_id or vice versa
                const nameMatch = status.name.toLowerCase().includes(campaignId.toLowerCase()) || 
                                 campaignId.toLowerCase().includes(status.name.toLowerCase());
                                 
                // Check if the campaign ID is part of the sub_id (sometimes sub_id might be a longer string containing the ID)
                const idInSubId = campaignId.includes(status.id);
                
                return nameMatch || idInSubId;
            });
        }
        
        return campaignStatus ? campaignStatus.is_active : null;
    };

    // Handle row click for detailed analysis
    const handleRowClick = (report) => {
        // Only proceed if we're not clicking on a button or icon
        setDetailCampaign({
            sub_id: report.sub_id,
            keyword: report.keyword
        });
        setShowDetailModal(true);
        console.debug('Opening detailed analysis for:', report.sub_id, report.keyword);
    };

    // Close the detail modal
    const handleCloseDetailModal = () => {
        setShowDetailModal(false);
    };

    // For debugging - log what's being rendered
    console.debug('CampaignTable rendering with:', {
        sortedReportsLength: sortedReports.length,
        selectedCampaign,
        isFiltered,
        totalReportsCount
    });

    // Determine the actual total count of reports
    const actualTotalCount = totalReportsCount > 0 ? totalReportsCount : reports.length;

    // Handle opening the detail modal
    const handleOpenDetailModal = (campaign) => {
        console.debug('Opening detail modal for campaign:', campaign);
        console.debug('Current dateRange:', dateRange);
        setDetailCampaign(campaign);
        setShowDetailModal(true);
    };

    return (
        <>
            <TableContainer 
                component={Paper} 
                elevation={3} 
                sx={{ 
                    borderRadius: '8px', 
                    bgcolor: '#1e2130',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                    maxHeight: '600px',
                    overflow: 'auto',
                    // Custom scrollbar styling
                    '&::-webkit-scrollbar': {
                        width: '8px',
                        height: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                        backgroundColor: 'rgba(0,0,0,0.1)',
                        borderRadius: '8px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        borderRadius: '8px',
                        '&:hover': {
                            backgroundColor: 'rgba(255,255,255,0.3)',
                        },
                    },
                    // For Firefox
                    scrollbarWidth: 'thin',
                    scrollbarColor: 'rgba(255,255,255,0.2) rgba(0,0,0,0.1)',
                }}
            >
                <Table stickyHeader size="small" sx={{ borderCollapse: 'collapse' }}>
                    <TableHead>
                        <TableRow sx={{ height: '36px' }}>
                            <TableCell 
                                sx={{ 
                                    bgcolor: '#2c3040', 
                                    color: 'white', 
                                    width: '50px', 
                                    padding: '2px 4px', 
                                    textAlign: 'center',
                                    fontSize: '0.85rem'
                                }}
                            >
                                <strong>Filter</strong>
                            </TableCell>
                            <TableCell 
                                sx={{ 
                                    bgcolor: '#2c3040', 
                                    color: 'white',
                                    cursor: 'pointer',
                                    '&:hover': { opacity: 0.8 },
                                    fontSize: '0.85rem'
                                }}
                                onClick={() => requestSort('sub_id')}
                            >
                                <strong>Sub ID{getSortDirectionIndicator('sub_id')}</strong>
                            </TableCell>
                            <TableCell 
                                sx={{ 
                                    bgcolor: '#2c3040', 
                                    color: 'white',
                                    cursor: 'pointer',
                                    '&:hover': { opacity: 0.8 },
                                    maxWidth: '200px', // Set maximum width for the keyword column
                                    width: '200px',     // Set fixed width for the keyword column
                                    fontSize: '0.85rem'
                                }}
                                onClick={() => requestSort('keyword')}
                            >
                                <strong>Keyword{getSortDirectionIndicator('keyword')}</strong>
                            </TableCell>
                            <TableCell 
                                sx={{ 
                                    bgcolor: '#2c3040', 
                                    color: 'white',
                                    width: '100px',
                                    textAlign: 'center',
                                    fontSize: '0.85rem'
                                }}
                            >
                                <strong>Status</strong>
                            </TableCell>
                            <TableCell 
                                sx={{ 
                                    bgcolor: '#2c3040', 
                                    color: 'white',
                                    cursor: 'pointer',
                                    '&:hover': { opacity: 0.8 },
                                    fontSize: '0.85rem'
                                }}
                                onClick={() => requestSort('tq')}
                            >
                                <strong>TQ{getSortDirectionIndicator('tq')}</strong>
                            </TableCell>
                            <TableCell 
                                sx={{ 
                                    bgcolor: '#2c3040', 
                                    color: 'white',
                                    cursor: 'pointer',
                                    '&:hover': { opacity: 0.8 },
                                    fontSize: '0.85rem'
                                }}
                                onClick={() => requestSort('taboola_clicks')}
                            >
                                <strong>Searches{getSortDirectionIndicator('taboola_clicks')}</strong>
                            </TableCell>
                            <TableCell 
                                sx={{ 
                                    bgcolor: '#2c3040', 
                                    color: 'white',
                                    cursor: 'pointer',
                                    '&:hover': { opacity: 0.8 },
                                    fontSize: '0.85rem'
                                }}
                                onClick={() => requestSort('conversions')}
                            >
                                <strong>Clicks{getSortDirectionIndicator('conversions')}</strong>
                            </TableCell>
                            <TableCell 
                                sx={{ 
                                    bgcolor: '#2c3040', 
                                    color: 'white',
                                    cursor: 'pointer',
                                    '&:hover': { opacity: 0.8 },
                                    fontSize: '0.85rem'
                                }}
                                onClick={() => requestSort('cvr')}
                            >
                                <strong>CVR (%){getSortDirectionIndicator('cvr')}</strong>
                            </TableCell>
                            <TableCell 
                                sx={{ 
                                    bgcolor: '#2c3040', 
                                    color: 'white',
                                    cursor: 'pointer',
                                    '&:hover': { opacity: 0.8 },
                                    fontSize: '0.85rem'
                                }}
                                onClick={() => requestSort('coverage')}
                            >
                                <strong>Coverage (%){getSortDirectionIndicator('coverage')}</strong>
                            </TableCell>
                            <TableCell 
                                sx={{ 
                                    bgcolor: '#2c3040', 
                                    color: 'white',
                                    cursor: 'pointer',
                                    '&:hover': { opacity: 0.8 },
                                    fontSize: '0.85rem'
                                }}
                                onClick={() => requestSort('cost')}
                            >
                                <strong>Cost{getSortDirectionIndicator('cost')}</strong>
                            </TableCell>
                            <TableCell 
                                sx={{ 
                                    bgcolor: '#2c3040', 
                                    color: 'white',
                                    cursor: 'pointer',
                                    '&:hover': { opacity: 0.8 },
                                    fontSize: '0.85rem'
                                }}
                                onClick={() => requestSort('cpc')}
                            >
                                <strong>CPC{getSortDirectionIndicator('cpc')}</strong>
                            </TableCell>
                            <TableCell 
                                sx={{ 
                                    bgcolor: '#2c3040', 
                                    color: 'white',
                                    cursor: 'pointer',
                                    '&:hover': { opacity: 0.8 },
                                    fontSize: '0.85rem'
                                }}
                                onClick={() => requestSort('payout')}
                            >
                                <Tooltip title="Payout is calculated as total_revenue divided by total clicks (Revenue per Click)">
                                    <Box component="span">
                                        <strong>Payout{getSortDirectionIndicator('payout')}</strong>
                                    </Box>
                                </Tooltip>
                            </TableCell>
                            <TableCell 
                                sx={{ 
                                    bgcolor: '#2c3040', 
                                    color: 'white',
                                    cursor: 'pointer',
                                    '&:hover': { opacity: 0.8 },
                                    fontSize: '0.85rem'
                                }}
                                onClick={() => requestSort('revenue')}
                            >
                                <strong>Revenue{getSortDirectionIndicator('revenue')}</strong>
                            </TableCell>
                            <TableCell 
                                sx={{ 
                                    bgcolor: '#2c3040', 
                                    color: 'white',
                                    cursor: 'pointer',
                                    '&:hover': { opacity: 0.8 },
                                    fontSize: '0.85rem'
                                }}
                                onClick={() => requestSort('roas')}
                            >
                                <strong>ROAS{getSortDirectionIndicator('roas')}</strong>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedReports.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={12} sx={{ textAlign: 'center', color: 'rgba(255,255,255,0.7)', bgcolor: '#1e2130' }}>
                                    No data available.
                                </TableCell>
                            </TableRow>
                        ) : (
                            sortedReports.map((report) => {
                                const isLowPerformance = hasLowPerformance(report);
                                return (
                                    <TableRow 
                                        key={`${report.sub_id}-${report.keyword}`}
                                        sx={{
                                            bgcolor: selectedCampaign === report.sub_id 
                                                ? 'rgba(30, 33, 48, 0.9)' 
                                                : isLowPerformance 
                                                    ? 'rgba(244, 67, 54, 0.15)' // Light red background for low performance
                                                    : '#1e2130',
                                            '&:hover': { bgcolor: '#2c3040', cursor: 'pointer' },
                                            color: 'white',
                                            borderLeft: isLowPerformance ? '4px solid #f44336' : 'none',
                                            height: '32px'
                                        }}
                                        onClick={(e) => {
                                            // Don't trigger row click if clicking on a button or icon
                                            if (e.target.tagName !== 'BUTTON' && 
                                                e.target.tagName !== 'svg' && 
                                                !e.target.closest('button')) {
                                                handleRowClick(report);
                                            }
                                        }}
                                    >
                                        <TableCell sx={{ 
                                            color: 'white', 
                                            width: '50px', 
                                            padding: '2px 4px',
                                            textAlign: 'center',
                                            fontSize: '0.85rem'
                                        }}>
                                            <Tooltip title={selectedCampaign === report.sub_id ? "Click to show all campaigns" : "Filter graph by this campaign"}>
                                                <IconButton 
                                                    onClick={() => handleFilterClick(report.sub_id)}
                                                    size="small"
                                                    sx={{ 
                                                        color: selectedCampaign === report.sub_id ? '#90caf9' : 'rgba(255, 255, 255, 0.7)',
                                                        bgcolor: selectedCampaign === report.sub_id ? 'rgba(144, 202, 249, 0.2)' : 'transparent',
                                                        border: selectedCampaign === report.sub_id ? '1px solid rgba(144, 202, 249, 0.5)' : 'none',
                                                        '&:hover': { 
                                                            bgcolor: 'rgba(144, 202, 249, 0.2)',
                                                            color: '#90caf9'
                                                        },
                                                        padding: '2px'
                                                    }}
                                                >
                                                    <FilterListIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell sx={{ 
                                            color: 'white',
                                            fontWeight: isLowPerformance ? 'bold' : 'normal',
                                            height: '100%',
                                            boxSizing: 'border-box',
                                            fontSize: '0.85rem',
                                            padding: '2px 6px',
                                            maxWidth: '120px',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                        }}>
                                            <Tooltip title={`${report.sub_id || '-'}${report.dates && report.dates.length > 1 ? ` (${report.dates.length} dates)` : ''}`}>
                                                <span>{report.sub_id || '-'}</span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell sx={{ 
                                            color: 'white',
                                            maxWidth: '200px',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            fontSize: '0.85rem',
                                            padding: '2px 6px'
                                        }}>
                                            <Tooltip title={report.keyword || '-'}>
                                                <span>{report.keyword || '-'}</span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell sx={{ 
                                            color: 'white',
                                            textAlign: 'center',
                                            padding: '2px 6px'
                                        }}>
                                            {getCampaignStatus(report.sub_id) !== null ? (
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    color={getCampaignStatus(report.sub_id) ? "success" : "error"}
                                                    onClick={() => handleToggleStatus(report.sub_id)}
                                                    disabled={togglingCampaigns[report.sub_id]}
                                                    sx={{ 
                                                        minWidth: '60px',
                                                        fontSize: '0.65rem',
                                                        py: 0,
                                                        px: 1,
                                                        height: '20px',
                                                        lineHeight: 1
                                                    }}
                                                >
                                                    {togglingCampaigns[report.sub_id] ? (
                                                        <CircularProgress size={12} color="inherit" />
                                                    ) : (
                                                        getCampaignStatus(report.sub_id) ? "Active" : "Paused"
                                                    )}
                                                </Button>
                                            ) : (
                                                <Chip 
                                                    label="Unknown" 
                                                    size="small" 
                                                    sx={{ 
                                                        bgcolor: 'rgba(255, 255, 255, 0.1)',
                                                        color: 'rgba(255, 255, 255, 0.7)',
                                                        fontSize: '0.65rem',
                                                        height: '20px'
                                                    }} 
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ 
                                            color: 'white',
                                            fontWeight: 'normal',
                                            height: '100%',
                                            boxSizing: 'border-box',
                                            fontSize: '0.85rem',
                                            padding: '2px 6px',
                                            textAlign: 'right'
                                        }}>
                                            {report.tq_status === 'missing' ? '-' : 
                                             report.tq_status === 'zero' ? '0' : 
                                             report.tq_status === 'negative' ? '-' : 
                                             formatNumber(report.tq, 0)}
                                        </TableCell>
                                        <TableCell sx={{ color: 'white', fontSize: '0.85rem', padding: '2px 6px', textAlign: 'right' }}>{formatNumber(report.taboola_clicks)}</TableCell>
                                        <TableCell sx={{ color: 'white', fontSize: '0.85rem', padding: '2px 6px', textAlign: 'right' }}>{formatNumber(report.conversions)}</TableCell>
                                        <TableCell sx={{ 
                                            color: report.cvr < 10 ? '#f44336' : report.cvr < 20 ? '#ff9800' : '#4caf50',
                                            height: '100%',
                                            boxSizing: 'border-box',
                                            fontSize: '0.85rem',
                                            padding: '2px 6px',
                                            textAlign: 'right'
                                        }}>
                                            <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', width: '100%' }}>
                                                {formatNumber(report.cvr, 2)}%
                                                {report.cvr < 10 && (
                                                    <Tooltip title="CVR is below the recommended threshold (10%)">
                                                        <WarningIcon color="error" fontSize="small" sx={{ ml: 1 }} />
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ 
                                            color: report.coverage < 80 ? '#f44336' : '#4caf50',
                                            height: '100%',
                                            boxSizing: 'border-box',
                                            fontSize: '0.85rem',
                                            padding: '2px 6px',
                                            textAlign: 'right'
                                        }}>
                                            <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', width: '100%' }}>
                                                {formatNumber(report.coverage, 2)}%
                                                {report.coverage < 80 && (
                                                    <Tooltip title="Coverage is below the recommended threshold (80%)">
                                                        <WarningIcon color="error" fontSize="small" sx={{ ml: 1 }} />
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ color: 'white', fontSize: '0.85rem', padding: '2px 6px', textAlign: 'right' }}>{formatCurrency(report.cost)}</TableCell>
                                        <TableCell sx={{ color: 'white', fontSize: '0.85rem', padding: '2px 6px', textAlign: 'right' }}>{formatNumber(report.cpc, 2)}</TableCell>
                                        <TableCell sx={{ 
                                            color: (report.total_revenue / report.clicks) < 0.10 ? '#f44336' : 'white',
                                            height: '100%',
                                            boxSizing: 'border-box',
                                            fontSize: '0.85rem',
                                            padding: '2px 6px',
                                            textAlign: 'right'
                                        }}>
                                            <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', width: '100%' }}>
                                                {/* Display the calculated payout value with 2 decimal places */}
                                                ${formatNumber(report.total_revenue / report.clicks, 2)}
                                                {(report.total_revenue / report.clicks) < 0.10 && (
                                                    <Tooltip title="Payout is below the recommended threshold ($0.10)">
                                                        <WarningIcon color="error" fontSize="small" sx={{ ml: 1 }} />
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ color: 'white', fontSize: '0.85rem', padding: '2px 6px', textAlign: 'right' }}>{formatCurrency(report.revenue)}</TableCell>
                                        <TableCell sx={{ 
                                            color: report.roas < 0.20 ? '#f44336' : report.roas < 1 ? '#ff9800' : report.roas < 1.5 ? '#ff9800' : '#4caf50',
                                            fontWeight: 'bold',
                                            height: '100%',
                                            boxSizing: 'border-box',
                                            fontSize: '0.85rem',
                                            padding: '2px 6px',
                                            textAlign: 'right'
                                        }}>
                                            <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', width: '100%' }}>
                                                {formatNumber(report.roas, 2)}
                                                {report.roas < 0.20 && (
                                                    <Tooltip title="ROAS is critically low (below 0.20)">
                                                        <WarningIcon color="error" fontSize="small" sx={{ ml: 1 }} />
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Campaign Detail Analysis Modal */}
            {showDetailModal && detailCampaign && (
                <Dialog 
                    open={showDetailModal} 
                    onClose={handleCloseDetailModal}
                    maxWidth="lg"
                    fullWidth
                    PaperProps={{
                        sx: {
                            bgcolor: '#1e2130',
                            color: 'white',
                            borderRadius: '8px',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                        }
                    }}
                >
                    <DialogTitle sx={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="h6">
                                Campaign Detail Analysis: {detailCampaign.sub_id} - {detailCampaign.keyword}
                            </Typography>
                            <IconButton onClick={handleCloseDetailModal} sx={{ color: 'white' }}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </DialogTitle>
                    <DialogContent sx={{ py: 2 }}>
                        <CampaignDetailAnalysis 
                            subId={detailCampaign.sub_id} 
                            keyword={detailCampaign.keyword}
                            globalDateRange={dateRange}
                        />
                    </DialogContent>
                </Dialog>
            )}
        </>
    );
};

export default CampaignTable; 